import { ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ehFisica, type Pagina, type Pessoa } from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { TelaCarregando } from '../componentes/carregando';
import { Aviso } from '../componentes/ui';
import { api } from '../lib/sessao';
import {
  FormularioFisica,
  FormularioJuridica,
  type CriarFisica,
  type CriarJuridica,
} from './pessoa-nova';

/*
  Edição de pessoa.

  É a MESMA tela do cadastro, com valores preenchidos — os formulários vêm de
  `pessoa-nova.tsx`, não são reescritos aqui. Dois componentes separados
  divergiriam no primeiro campo acrescentado, e o sintoma seria um campo que
  existe no cadastro e some na correção.

  ## Por que a natureza não aparece para trocar
  No cadastro, escolher entre física e jurídica é a primeira decisão. Aqui ela
  não existe: o servidor recusa a troca (`natureza_imutavel`) porque o `id` já
  está referenciado em participações e administrações de contratos ASSINADOS —
  uma física que virasse jurídica manteria o mesmo id, e o documento
  continuaria dizendo "brasileiro, casado, empresário" sobre uma razão social.
  Mostrar um seletor que o servidor vai recusar seria oferecer um caminho sem
  saída.

  ## De onde vem a pessoa
  Da MESMA consulta que a lista usa (`['pessoas', 'consulta']`), e não de um
  `GET /pessoas/:id` — que não existe. Quem chega aqui pela lista encontra o
  dado já em cache e o formulário abre preenchido na hora.
*/

export function PessoaEditar(): JSX.Element {
  const { id } = useParams<{ id: string }>();

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });

  const pessoa = pessoasQuery.data?.itens.find((p) => p.id === id);

  if (pessoasQuery.isPending) {
    return (
      <PaginaShell titulo="Editar pessoa">
        <TelaCarregando />
      </PaginaShell>
    );
  }

  if (!pessoa) {
    return (
      <PaginaShell titulo="Editar pessoa">
        <Aviso>
          Essa pessoa não está na leva carregada. Volte para a lista e abra pela busca.
        </Aviso>
        <div className="mt-3">
          <Link to="/pessoas" className="text-marca-600 text-sm hover:underline">
            Voltar para a lista →
          </Link>
        </div>
      </PaginaShell>
    );
  }

  return (
    <PaginaShell
      titulo={`Editar ${ehFisica(pessoa) ? pessoa.nome : pessoa.razaoSocial}`}
      subtitulo={`Cadastro #${pessoa.codigo} — a natureza não muda depois de cadastrada`}
      acoes={
        <Link
          to="/pessoas"
          className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Voltar
        </Link>
      }
    >
      {ehFisica(pessoa) ? (
        <FormularioFisica id={pessoa.id} inicial={paraFormularioFisica(pessoa)} />
      ) : (
        <FormularioJuridica id={pessoa.id} inicial={paraFormularioJuridica(pessoa)} />
      )}
    </PaginaShell>
  );
}

/**
 * `Pessoa` (o que o servidor devolve) → o que o formulário edita.
 *
 * Tira `id`, `tenantId` e `codigo`: são do servidor, e mandá-los de volta no
 * corpo seria oferecer ao cliente a chance de escolher o próprio número — ou,
 * pior, o tenant de outro escritório.
 */
function paraFormularioFisica(p: Extract<Pessoa, { natureza: 'FISICA' }>): CriarFisica {
  const { id: _id, tenantId: _t, codigo: _c, ...resto } = p;
  return resto;
}

function paraFormularioJuridica(
  p: Extract<Pessoa, { natureza: 'JURIDICA' }>,
): CriarJuridica {
  const { id: _id, tenantId: _t, codigo: _c, ...resto } = p;
  return resto;
}
